// ─── Onglet FAMILLE (My Calendar) ─────────────────────────────────
// Hub des fonctions de COLLABORATION (indépendantes du calendrier actif) :
// Demandes (actif) + écrans « à venir » de la migration MoPCal, avec leur
// périmètre. Rendu = templates Core (HubTuiles / AVenir) ; données = famille.js.
import React, { useEffect, useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { HubTuiles, AVenir, useT, useAuth, useFonctions, usePeutUtiliser, useResetOnglet, palierLabel, EcranMessagerie, cleCible, useEspace, membresDe } from '@mop/core';
import { EcranMenus, EcranCourses, EcranRepas } from '@mop/feature-meals';
import { FAMILLE_SECTIONS } from './data/famille';
import { useCalendrierActif } from './CalendrierActifContext';
import { ecouterDemandes } from './services/demandes';
import { lireParents } from './services/garde';
import EcranDemandes from './EcranDemandes';
import EcranLieux from './EcranLieux';
import EcranContacts from './EcranContacts';
import EcranExport from './EcranExport';
import EcranJournal from './EcranJournal';
import EcranHistorique from './EcranHistorique';
import EcranStatistiques from './EcranStatistiques';
import EcranListes from './EcranListes';

export default function EcranFamille({ ongletCle = 'Famille' }) {
  const t = useT();
  const { user } = useAuth();
  const { calIdActif, calendriers } = useCalendrierActif();
  // La messagerie a besoin de l'espace : sélection des membres à la création
  // d'une discussion, et propriétaire pour l'administration d'une orpheline.
  const { espaceActif } = useEspace();
  const membresEspace = useMemo(() => membresDe(espaceActif), [espaceActif]);
  const [ecran, setEcran] = useState(null);          // tuile « à venir » ouverte
  const [vueLieux, setVueLieux] = useState(false);   // écran Lieux (réel, porté)
  const [vueContacts, setVueContacts] = useState(false);   // écran Contacts (réel, porté)
  const [vueExport, setVueExport] = useState(false);       // écran Export (réel, porté)
  const [vueMenu, setVueMenu] = useState(false);   // Planning des menus (réel, porté — @mop/feature-meals)
  const [vueRepasCompo, setVueRepasCompo] = useState(false);   // Compositions de repas (réel, porté — @mop/feature-meals)
  const [vueCourses, setVueCourses] = useState(false);   // Liste de courses (réel, porté — @mop/feature-meals)
  const [vueMessagerie, setVueMessagerie] = useState(false);   // Messagerie (réelle, portée — @mop/core)
  const [vueJournal, setVueJournal] = useState(false);     // écran Journal de bord (réel, porté)
  const [vueHistorique, setVueHistorique] = useState(false);   // écran Historique (réel, porté)
  const [vueStats, setVueStats] = useState(false);   // écran Statistiques (réel, porté)
  const [vueListes, setVueListes] = useState(false);   // écran Listes (réel, porté)
  const [vueDemandes, setVueDemandes] = useState(false);
  const [demandes, setDemandes] = useState([]);
  const [erreurDemandes, setErreurDemandes] = useState('');
  const [parents, setParents] = useState(null);

  // Toucher l'onglet deja actif REVIENT au hub (niveau 0) : on ferme l'ecran
  // ouvert par-dessus. Au niveau 0, le geste ne fait rien — c'est deja la.
  useResetOnglet(ongletCle, () => {
    setEcran(null); setVueLieux(false); setVueContacts(false); setVueDemandes(false); setVueExport(false); setVueMenu(false); setVueRepasCompo(false); setVueCourses(false); setVueMessagerie(false); setVueJournal(false); setVueHistorique(false); setVueStats(false); setVueListes(false);
  });

  // Demandes du calendrier ACTIF (même workflow que l'onglet Calendrier).
  useEffect(() => {
    if (!calIdActif) { setDemandes([]); setErreurDemandes(''); return undefined; }
    lireParents(calIdActif).then(setParents).catch(() => {});
    setErreurDemandes('');
    return ecouterDemandes(calIdActif, setDemandes, setErreurDemandes);
  }, [calIdActif]);

  // Une tuile est ACCESSIBLE si sa fonction est incluse au forfait ET si
  // l'écran est déjà porté. Verrouillée, elle indique le palier qui la
  // débloque ; portée mais pas encore migrée, elle ouvre son périmètre.
  const peut = usePeutUtiliser();
  const { catalogue } = useFonctions();
  const palierDe = (fn) => {
    const def = (catalogue?.byKey || {})[fn];
    return def?.palier ? palierLabel(def.palier) : '';
  };

  const sections = FAMILLE_SECTIONS.map(sec => ({
    cle: sec.cle,                       // sert de clé d'ORDRE (réagencement mémorisé)
    titre: t(`famille.sec.${sec.cle}`),
    tuiles: sec.tuiles.map(tu => {
      const inclus = !tu.fn || peut(tu.fn);
      return {
        cle: tu.cle, emoji: tu.emoji, actif: inclus,
        titre: t(`famille.t.${tu.cle}`),
        desc: tu.desc ? t(`famille.${tu.desc}`) : '',
        palier: inclus ? '' : palierDe(tu.fn),
        badge: tu.cle === 'demandes' ? demandes.filter(d => (d.statut || 'en_attente') === 'en_attente').length : 0,
        onPress: () => { if (!inclus) return; tu.ecran === 'lieux' ? setVueLieux(true) : tu.ecran === 'contacts' ? setVueContacts(true) : tu.ecran === 'export' ? setVueExport(true) : tu.ecran === 'menu' ? setVueMenu(true) : tu.ecran === 'repas' ? setVueRepasCompo(true) : tu.ecran === 'courses' ? setVueCourses(true) : tu.ecran === 'messagerie' ? setVueMessagerie(true) : tu.ecran === 'journal' ? setVueJournal(true) : tu.ecran === 'historique' ? setVueHistorique(true) : tu.ecran === 'statistiques' ? setVueStats(true) : tu.ecran === 'listes' ? setVueListes(true) : tu.porte ? setVueDemandes(true) : setEcran(tu); },
      };
    }),
  }));

  return (
    <View style={{ flex: 1 }}>
      <HubTuiles titre={t('famille.titre')} emoji="👪" sections={sections} cleOrdre="famille" />
      {/* Messagerie : écran RÉEL porté (Core), posé par-dessus le hub. */}
      {vueMessagerie && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranMessagerie
            cible={calIdActif ? cleCible('calendrier', calIdActif) : null}
            /* TOUS les calendriers visibles : la messagerie ne doit pas
               dependre de celui qui est actif pour montrer une conversation. */
            cibles={(calendriers || []).map(c => ({ cle: cleCible('calendrier', c.id), nom: c.nom }))}
            membres={membresEspace}
            proprietaireEspace={espaceActif?.creePar || null}
            onRetour={() => setVueMessagerie(false)}
          />
        </View>
      )}

      {/* Écran « à venir » posé PAR-DESSUS le hub : le balayage-retour révèle
          la page d'origine en dessous (et non un fond vide). */}
      {ecran && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <AVenir emoji={ecran.emoji} titre={t(`famille.t.${ecran.cle}`)}
            destination={t(`avenir.dest.${ecran.dest}`)}
            fonctions={t(`avenir.f.${ecran.cle}`)}
            onRetour={() => setEcran(null)} />
        </View>
      )}
      {/* Lieux : écran RÉEL porté (recherche d'adresse, itinéraire), posé par-dessus le hub. */}
      {vueLieux && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranLieux calId={calIdActif} onFermer={() => setVueLieux(false)} />
        </View>
      )}
      {vueContacts && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranContacts calId={calIdActif} onFermer={() => setVueContacts(false)} />
        </View>
      )}
      {/* Journal de bord : notes datées du calendrier ACTIF, même montage que
          Contacts et Lieux (calque posé par-dessus le hub). */}
      {vueJournal && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranJournal calId={calIdActif} onFermer={() => setVueJournal(false)} />
        </View>
      )}
      {vueExport && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranExport onRetour={() => setVueExport(false)} />
        </View>
      )}
      {vueMenu && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          {/* calId : les SEMAINES posent les repas dans le calendrier actif —
              les menus, eux, vivent au niveau de l espace. */}
          <EcranMenus calId={calIdActif} onFermer={() => setVueMenu(false)} />
        </View>
      )}
      {vueRepasCompo && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          {/* Pas de calId : les compositions de repas vivent au niveau de
              l'ESPACE (useEspace interne), comme les menus et les courses. */}
          <EcranRepas onFermer={() => setVueRepasCompo(false)} />
        </View>
      )}
      {vueCourses && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          {/* Pas de calId : la liste de courses vit au niveau de l'ESPACE
              (useEspace interne), comme les menus. */}
          <EcranCourses onFermer={() => setVueCourses(false)} />
        </View>
      )}
      {vueHistorique && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranHistorique calId={calIdActif} onFermer={() => setVueHistorique(false)} />
        </View>
      )}
      {vueStats && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranStatistiques calId={calIdActif} onFermer={() => setVueStats(false)} />
        </View>
      )}
      {vueListes && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranListes calId={calIdActif} onFermer={() => setVueListes(false)} />
        </View>
      )}
      {vueDemandes && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranDemandes calId={calIdActif} demandes={demandes} erreurLecture={erreurDemandes}
            user={user} parents={parents} onFermer={() => setVueDemandes(false)} />
        </View>
      )}
    </View>
  );
}
